/**
 * /creatives — Creative Performance drawn natively from Supabase (spec
 * 2026-10-15-native-creative-performance-design). Composition only: reads in
 * useCreativeData, rows in buildCreativeView, box 1 in CreativeControls, box 2
 * in CreativeTable. Period, objective and currency are the dashboards' shared
 * filters (useDashboardFilters); the channel selection is D1's remembered one;
 * sort, format and "แสดงทั้งหมด" are D3's own URL keys (useCreativeUrl). The
 * scope is D2's (useChannelPageScope, useScopeArrival), so scope=all on a link
 * works here too. Export offers PDF and CSV; the chat runs in mode 'creatives'
 * over the table and its Top and Fatigue creatives (CreativeChat, spec
 * 2026-10-21 d3-d4 chat §5).
 *
 * @module pages/CreativePerformance
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { YDMPageHero } from '@/components/brand';
import { CreativeControls } from '@/components/creatives/CreativeControls';
import { CreativeTable } from '@/components/creatives/CreativeTable';
import { DataStatusPill } from '@/components/looker/DataStatusPill';
import { ExportMenu } from '@/components/looker/ExportMenu';
import MediaChatBubble from '@/components/MediaChatBubble';
import { OverviewFilterBar } from '@/components/overview/OverviewFilterBar';
import { PrintHeader } from '@/components/overview/PrintHeader';
import { CREATIVE_ADVISOR } from '@/config/agentProfiles';
import { useChannelFilter } from '@/contexts/ChannelFilterContext';
import { useChannelPageScope, useScopeArrival, type PageScope } from '@/hooks/useChannelPageScope';
import { useCreativeData, type CreativeData } from '@/hooks/useCreativeData';
import { useCreativeUrl } from '@/hooks/useCreativeUrl';
import { useDashboardFilters } from '@/hooks/useDashboardFilters';
import { useHeldChatContext } from '@/hooks/useHeldChatContext';
import { PRINT_FRAME_WIDTH_PX, usePrintFrame } from '@/hooks/usePrintFrame';
import { AD_CHANNEL_IDS } from '@/services/channelFilter';
import { buildCreativeChatContext } from '@/services/creatives/creativeChatContext';
import { creativeCsvReady, downloadCreativeCsv } from '@/services/creatives/creativeCsv';
import { channelFilterText, footnotesOf, objectiveFilterText } from '@/services/creatives/creativeText';
import type { CreativeFilters, CreativeView } from '@/services/creatives/creativeTypes';
import { buildCreativeView, creativeDataDateOf } from '@/services/creatives/creativeView';
import { csvLabelsFrom } from '@/services/looker/lookerCsv';
import { addDays, bangkokToday } from '@/services/looker/lookerDateRange';
import { DASH } from '@/services/overview/overviewFormat';

const BANNER_CLASS = 'rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-ink-950';
const PRINT_LINE_CLASS = 'hidden text-xs text-neutral-800 print:block';

/**
 * Landscape A4 for this page only, and box 2 fitted to its card on paper (final review I1, I2). OverviewTable keeps its
 * screen type, padding and one-line headers on paper, which ran two columns off the card at the 1,040 px frame; the
 * attribute-plus-type selectors out-rank its utilities whatever the order. The rows stay whole across pages. The rules
 * leave with the page.
 */
const PRINT_CSS = [
  '@page { size: A4 landscape; margin: 10mm; }',
  '@media print {',
  '  [data-overview-box="2"] table { font-size: 11px; }',
  '  [data-overview-box="2"] th, [data-overview-box="2"] td { padding-left: 6px; padding-right: 6px; }',
  '  [data-overview-box="2"] th { white-space: normal; }',
  '  [data-overview-box="2"] tr { break-inside: avoid; }',
  '}',
].join('\n');

/** D1's remembered channel selection, as display-ordered ids; [] = every channel (as D1's page reads it). */
const useChannels = (): string[] => {
  const { selected, filtering } = useChannelFilter();
  const key = filtering ? AD_CHANNEL_IDS.filter((id) => selected.has(id)).join(',') : '';
  return useMemo(() => (key ? key.split(',') : []), [key]);
};

interface PageState {
  filters: ReturnType<typeof useDashboardFilters>;
  url: ReturnType<typeof useCreativeUrl>;
  creativeFilters: CreativeFilters;
  data: CreativeData;
  view: CreativeView;
  dataDate: string | null;
}

/** R4's order of URL writers, then the reads, then the view. Only the scope and the period reach a read (spec §2.2). */
const useCreativePage = (page: PageScope): PageState => {
  const filters = useDashboardFilters();
  const url = useCreativeUrl(filters.shared);
  // Last, so the arrival replace is the final writer of the first pass and carries the shared keys; D3's own keys
  // settle one pass later (R4, D2's I2).
  useScopeArrival(page, filters.shared);
  const channels = useChannels();
  const data = useCreativeData(page.scope, filters.shared.period);
  const { objective, currency } = filters.shared;
  const { sort, format, showAll } = url.creative;
  const creativeFilters = useMemo<CreativeFilters>(
    () => ({ channels, objective, currency, format, sort, showAll }), [channels, objective, currency, format, sort, showAll],
  );
  // Bangkok's yesterday on the day the page is open: age and status (R10, R11), whatever period is shown.
  const yesterday = addDays(bangkokToday(), -1);
  const view = useMemo(
    () => buildCreativeView({ facts: data.facts, mapping: data.mapping, filters: creativeFilters, yesterday }),
    [data.facts, data.mapping, creativeFilters, yesterday],
  );
  return { filters, url, creativeFilters, data, view, dataDate: creativeDataDateOf(data.freshness, view) };
};

interface PageExport {
  framing: boolean;
  onPdf: () => void;
  pdfDisabled: boolean;
  onCsv: () => void;
  csvDisabled: boolean;
}

/** R16: both items wait for the table; PDF also for the print frame, CSV also for the freshness read. */
const usePageExport = (page: PageScope, state: PageState): PageExport => {
  const { t } = useTranslation();
  const printFrame = usePrintFrame();
  const labels = useMemo(() => csvLabelsFrom(t), [t]);
  const { data, view, creativeFilters, dataDate } = state;
  const windows = data.creativeWindows;
  const onCsv = (): void => {
    if (!windows) return;
    downloadCreativeCsv({
      allWorkspaces: page.showAll, workspaceName: page.workspaceName, windows, view, filters: creativeFilters,
      mapping: data.mapping, freshness: data.freshness, dataDate,
    }, labels, t);
  };
  return {
    framing: printFrame.framing,
    onPdf: printFrame.start,
    pdfDisabled: view.table.status !== 'ready' || printFrame.framing,
    onCsv,
    csvDisabled: windows === null || !creativeCsvReady(view, data.freshness),
  };
};

/** The master's switch, as on D1 and D2. */
const AllWorkspacesToggle: React.FC<{ checked: boolean; onChange: (on: boolean) => void }> = ({ checked, onChange }) => {
  const { t } = useTranslation();
  return (
    <label className="inline-flex items-center gap-2 text-sm font-medium text-ink-900">
      <input type="checkbox" className="h-4 w-4 rounded border-neutral-400 accent-ink-950" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {t('looker.viewAllWorkspaces')}
    </label>
  );
};

interface PartProps {
  page: PageScope;
  state: PageState;
}

/** Row F (spec §3.2), D1's control band: the filters, the data pill, the cut, Export, the switch, the period, the banner. */
const ControlBand: React.FC<PartProps & { exporter: PageExport }> = ({ page, state, exporter }) => {
  const { t } = useTranslation();
  const { filters: { shared, setShared }, data, view, dataDate } = state;
  const w = data.creativeWindows;
  return (
    <section data-creatives-controls className="print:hidden space-y-4 rounded-2xl border border-neutral-200 border-l-[5px] border-l-primary-500 bg-white px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <OverviewFilterBar
          token={shared.period} onToken={(period) => setShared({ period })}
          objective={shared.objective} onObjective={(objective) => setShared({ objective })}
          money={view.money} onCurrency={(currency) => setShared({ currency })}
          disabled={data.facts.status === 'loading'}
        />
        <div className="flex flex-wrap items-center gap-3">
          <DataStatusPill latestDataDate={dataDate} state={data.freshness} />
          {data.windows?.cutApplied && <span className="text-xs font-medium text-neutral-800">{t('looker.dataStatus.unsettled')}</span>}
          <ExportMenu pdfDisabled={exporter.pdfDisabled} onPdf={exporter.onPdf} csvDisabled={exporter.csvDisabled} onCsv={exporter.onCsv} />
          {page.isMaster && <AllWorkspacesToggle checked={page.viewAll} onChange={page.setViewAll} />}
        </div>
      </div>
      {w && <p className="text-xs text-neutral-700">{t('creatives.periodLine', { start: w.start, end: w.end })}</p>}
      {page.showAll && <p role="status" className={BANNER_CLASS}>{t('looker.allWorkspacesBanner')}</p>}
    </section>
  );
};

/** R20: the band does not print, so its period and filters print as two lines. */
const PrintLines: React.FC<{ state: PageState }> = ({ state }) => {
  const { t } = useTranslation();
  const { data: { creativeWindows: w }, creativeFilters: f, view } = state;
  return (
    <>
      {w && <p className={PRINT_LINE_CLASS}>{t('creatives.periodLine', { start: w.start, end: w.end })}</p>}
      <p className={PRINT_LINE_CLASS}>
        {t('creatives.printFilters', {
          channels: channelFilterText(f.channels, t), objective: objectiveFilterText(f.objective, t), currency: view.money?.currency ?? DASH,
        })}
      </p>
    </>
  );
};

/** Boxes 1 and 2, once the page has a scope and a window. */
const CreativeBody: React.FC<PartProps> = ({ page, state }) => {
  const { t } = useTranslation();
  const { data, view, creativeFilters, url: { creative, setCreative } } = state;
  if (!page.scope) return <p className="text-sm text-neutral-700">{t('looker.loading')}</p>;
  if (!data.creativeWindows) return <p className="text-sm text-neutral-800">{t('looker.badDateRange')}</p>;
  const notes = footnotesOf({
    resultEnd: data.creativeWindows.resultEnd, mappingFailed: data.mapping?.failed === true, unknownCurrency: view.unknownCurrency,
    // Every row the filters leave, as the file writes them: a creative counted by reach is Awareness under Reach.
    reach: view.table.status === 'ready' && view.table.data.rows.some((r) => r.metric === 'reach'),
  }, t);
  return (
    <>
      <CreativeControls sort={creative.sort} onSort={(sort) => setCreative({ sort })} format={creative.format} onFormat={(format) => setCreative({ format })} />
      <CreativeTable
        state={view.table} currency={view.money?.currency ?? null} scope={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? ''}
        filters={creativeFilters} notes={notes} showAll={creative.showAll} onShowAll={(on) => setCreative({ showAll: on })} onRetry={data.retry}
      />
    </>
  );
};

/**
 * The bubble in mode 'creatives' with CREATIVE_ADVISOR (spec 2026-10-21 d3-d4 chat §5), told once the page settled: a
 * scope, a period, the table read (ready or failed; it stays loading until the Result definition is read too) and the
 * statuses read. Keyed by its scope, so another workspace or the all-workspaces switch starts a new conversation. A
 * filter, the sort, the period or Retry keeps it: while the page has no new data yet (its reads are loading, or it
 * refuses the period) the chat holds the last data and takes no new question.
 */
const CreativeChat: React.FC<PartProps> = ({ page, state }) => {
  const { t } = useTranslation();
  const { data, view, creativeFilters, dataDate } = state;
  const { windows, creativeWindows } = data;
  const settled = page.scope !== null && windows !== null && creativeWindows !== null
    && view.table.status !== 'loading' && data.freshness.status !== 'loading';
  const context = useMemo(() => (settled && windows && creativeWindows ? buildCreativeChatContext({
    allWorkspaces: page.showAll, workspaceName: page.workspaceName, windows: creativeWindows, previous: windows.previous,
    view, filters: creativeFilters, mapping: data.mapping, freshness: data.freshness, dataDate,
  }, t) : null), [
    settled, windows, creativeWindows, page.showAll, page.workspaceName, view, creativeFilters, data.mapping, data.freshness, dataDate, t,
  ]);
  const scope = `creatives:${page.showAll ? 'all' : page.workspaceId ?? ''}`;
  const held = useHeldChatContext(context, scope);
  if (!held.context) return null;
  return (
    <div className="print:hidden">
      <MediaChatBubble
        key={scope}
        mode="creatives"
        lookerContext={held.context}
        contextPending={held.pending}
        scope={scope}
        title={t('creatives.chatTitle')}
        subtitle={t('looker.chatSubtitle')}
        guidedQuestions={CREATIVE_ADVISOR.guidedQuestions}
      />
    </div>
  );
};

const CreativePerformance: React.FC = () => {
  const { t } = useTranslation();
  const page = useChannelPageScope();
  const state = useCreativePage(page);
  const exporter = usePageExport(page, state);
  return (
    <div className="space-y-4 p-4 md:p-6 print:p-0">
      <div className="print:hidden">
        <YDMPageHero
          eyebrow={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? undefined}
          headline={t('creatives.title')} subtitle={t('creatives.subtitle')} className="mb-2"
        />
      </div>
      <PrintHeader
        title={t('creatives.title')}
        scope={page.showAll ? t('looker.allWorkspaces') : page.workspaceName ?? null}
        dataDate={state.dataDate}
      />
      <ControlBand page={page} state={state} exporter={exporter} />
      <PrintLines state={state} />
      <div
        className="space-y-4 [-webkit-print-color-adjust:exact] [print-color-adjust:exact]"
        style={exporter.framing ? { width: PRINT_FRAME_WIDTH_PX, maxWidth: '100%' } : undefined}
      >
        <style>{PRINT_CSS}</style>
        <CreativeBody page={page} state={state} />
      </div>
      <CreativeChat page={page} state={state} />
    </div>
  );
};

export default CreativePerformance;
